import { formatUsLocaleDate, parseAppDate } from '@/utils/date';

export type DateRangeOffsetUnit = 'days' | 'months' | 'years';

export interface DateRangeOffset {
  unit: DateRangeOffsetUnit;
  amount: number;
}

/**
 * Subtracts a lookback offset from a date (mutates and returns the same instance).
 */
export function subtractDateOffset(date: Date, offset: DateRangeOffset): Date {
  switch (offset.unit) {
    case 'days':
      date.setDate(date.getDate() - offset.amount);
      break;
    case 'months':
      date.setMonth(date.getMonth() - offset.amount);
      break;
    case 'years':
      date.setFullYear(date.getFullYear() - offset.amount);
      break;
  }
  return date;
}

/**
 * Standard date range shortcuts used across the app.
 *
 * Prefix semantics:
 * - "Trailing*": rolling periods ending today (e.g. Trailing30Days = last 30 days up to today).
 * - "Open*": the current calendar period (e.g. OpenYear = Jan 1 of this year through today).
 * - "Closed*": the most recent fully completed calendar period (e.g. ClosedYear = full previous year).
 */
export enum DataRangeFilterEnum {
  Trailing7Days = 'Trailing 7 days',
  Trailing30Days = 'Trailing 30 days',
  Trailing90Days = 'Trailing 90 days',
  Trailing1Month = 'Trailing 1 Month',
  Trailing3Months = 'Trailing 3 Months',
  Trailing6Months = 'Trailing 6 Months',
  Trailing9Months = 'Trailing 9 Months',
  Trailing1Year = 'Trailing 1 Year',
  Trailing2Years = 'Trailing 2 Years',
  Trailing3Years = 'Trailing 3 Years',
  Trailing5Years = 'Trailing 5 Years',
  OpenQuarter = 'Current Quarter (open)',
  ClosedQuarter = 'Last Quarter (closed)',
  OpenYear = 'Current Year (open)',
  ClosedYear = 'Last Year (closed)',
  AllTime = 'All Time',
  /** Used when the user picks a date range that is not one of the presets above. */
  Custom = 'Custom Date Range',
}

/**
 * Floor "All Time" asks the API for. It predates any real transaction, so the
 * response covers everything the account has — and a range starting here is how
 * a consumer recognises All Time, since by then only the dates survive.
 */
export const ALL_TIME_START_DATE = '01/01/1900';

/**
 * Whether a resolved range is the open-ended "All Time" one.
 *
 * Compares the calendar day rather than the string: useControlledDates hands a
 * host's dates back in whatever format they arrived in, so an embeddable host
 * passing the documented `YYYY-MM-DD` gives `1900-01-01` for the same range.
 */
export function isAllTimeStartDate(startDate?: string | null): boolean {
  if (!startDate) return false;
  if (startDate === ALL_TIME_START_DATE) return true;

  const parsed = parseAppDate(startDate);
  return parsed
    ? parsed.isSame(parseAppDate(ALL_TIME_START_DATE), 'day')
    : false;
}

export interface DateRangeFilterOption {
  filter: DataRangeFilterEnum;
  label: string;
}

/** Rolling lookback presets (subset of {@link DataRangeFilterEnum}). */
export const TRAILING_DATA_RANGE_FILTER_ORDER = [
  DataRangeFilterEnum.Trailing7Days,
  DataRangeFilterEnum.Trailing30Days,
  DataRangeFilterEnum.Trailing90Days,
  DataRangeFilterEnum.Trailing1Month,
  DataRangeFilterEnum.Trailing3Months,
  DataRangeFilterEnum.Trailing6Months,
  DataRangeFilterEnum.Trailing9Months,
  DataRangeFilterEnum.Trailing1Year,
  DataRangeFilterEnum.Trailing2Years,
  DataRangeFilterEnum.Trailing3Years,
  DataRangeFilterEnum.Trailing5Years,
] as const;

export type TrailingDataRangeFilter =
  (typeof TRAILING_DATA_RANGE_FILTER_ORDER)[number];

const CALENDAR_AND_OTHER_DATE_RANGE_FILTER_ORDER = [
  DataRangeFilterEnum.OpenQuarter,
  DataRangeFilterEnum.ClosedQuarter,
  DataRangeFilterEnum.OpenYear,
  DataRangeFilterEnum.ClosedYear,
  DataRangeFilterEnum.AllTime,
  DataRangeFilterEnum.Custom,
] as const;

/** Full shortcut order for {@link dateRangeFilterOptions} and DateRange picker shortcuts. */
export const dateRangeFilterOrder: DataRangeFilterEnum[] = [
  ...TRAILING_DATA_RANGE_FILTER_ORDER,
  ...CALENDAR_AND_OTHER_DATE_RANGE_FILTER_ORDER,
];

export function getDataRangeFilterLabel(filter: DataRangeFilterEnum): string {
  switch (filter) {
    case DataRangeFilterEnum.Trailing7Days:
      return 'Last 7 days';
    case DataRangeFilterEnum.Trailing30Days:
      return 'Last 30 days';
    case DataRangeFilterEnum.Trailing90Days:
      return 'Last 90 days';
    case DataRangeFilterEnum.Trailing1Month:
      return 'Last Month';
    case DataRangeFilterEnum.Trailing3Months:
      return 'Last 3 Months';
    case DataRangeFilterEnum.Trailing6Months:
      return 'Last 6 Months';
    case DataRangeFilterEnum.Trailing9Months:
      return 'Last 9 Months';
    case DataRangeFilterEnum.Trailing1Year:
      return 'Last Year';
    case DataRangeFilterEnum.Trailing2Years:
      return 'Last 2 Years';
    case DataRangeFilterEnum.Trailing3Years:
      return 'Last 3 Years';
    case DataRangeFilterEnum.Trailing5Years:
      return 'Last 5 Years';
    default:
      return filter;
  }
}

export const dateRangeFilterOptions: DateRangeFilterOption[] =
  dateRangeFilterOrder.map((filter) => ({
    filter,
    label: getDataRangeFilterLabel(filter),
  }));

export function getDataRangeFilterFromLabel(
  label: string,
): DataRangeFilterEnum | undefined {
  return dateRangeFilterOptions.find((option) => option.label === label)
    ?.filter;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

/** Rolling lookback offsets keyed by {@link TRAILING_DATA_RANGE_FILTER_ORDER}. */
export const DATA_RANGE_FILTER_OFFSETS: Record<
  TrailingDataRangeFilter,
  DateRangeOffset
> = {
  [DataRangeFilterEnum.Trailing7Days]: { unit: 'days', amount: 7 },
  [DataRangeFilterEnum.Trailing30Days]: { unit: 'days', amount: 30 },
  [DataRangeFilterEnum.Trailing90Days]: { unit: 'days', amount: 90 },
  [DataRangeFilterEnum.Trailing1Month]: { unit: 'months', amount: 1 },
  [DataRangeFilterEnum.Trailing3Months]: { unit: 'months', amount: 3 },
  [DataRangeFilterEnum.Trailing6Months]: { unit: 'months', amount: 6 },
  [DataRangeFilterEnum.Trailing9Months]: { unit: 'months', amount: 9 },
  [DataRangeFilterEnum.Trailing1Year]: { unit: 'years', amount: 1 },
  [DataRangeFilterEnum.Trailing2Years]: { unit: 'years', amount: 2 },
  [DataRangeFilterEnum.Trailing3Years]: { unit: 'years', amount: 3 },
  [DataRangeFilterEnum.Trailing5Years]: { unit: 'years', amount: 5 },
};

export function isTrailingDataRangeFilter(
  filter: DataRangeFilterEnum,
): filter is TrailingDataRangeFilter {
  return (
    TRAILING_DATA_RANGE_FILTER_ORDER as readonly DataRangeFilterEnum[]
  ).includes(filter);
}

function getTrailingDateRange(filter: TrailingDataRangeFilter): DateRange {
  const today = new Date();
  const startDate = subtractDateOffset(
    new Date(today),
    DATA_RANGE_FILTER_OFFSETS[filter],
  );
  return {
    startDate: formatUsLocaleDate(startDate),
    endDate: formatUsLocaleDate(today),
  };
}

export function getDateRangeFromFilter(filter: DataRangeFilterEnum): DateRange {
  const today = new Date();
  const currentYear = today.getFullYear();

  if (isTrailingDataRangeFilter(filter)) {
    return getTrailingDateRange(filter);
  }

  switch (filter) {
    case DataRangeFilterEnum.OpenQuarter: {
      const quarter = Math.floor(today.getMonth() / 3);
      const startDate = new Date(currentYear, quarter * 3, 1);
      const endDate = new Date(currentYear, (quarter + 1) * 3, 0);
      return {
        startDate: formatUsLocaleDate(startDate),
        endDate: formatUsLocaleDate(endDate),
      };
    }

    case DataRangeFilterEnum.ClosedQuarter: {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      const year = quarter < 0 ? currentYear - 1 : currentYear;
      const actualQuarter = quarter < 0 ? 3 : quarter;
      const startDate = new Date(year, actualQuarter * 3, 1);
      const endDate = new Date(year, (actualQuarter + 1) * 3, 0);
      return {
        startDate: formatUsLocaleDate(startDate),
        endDate: formatUsLocaleDate(endDate),
      };
    }

    case DataRangeFilterEnum.ClosedYear:
      return {
        startDate: `01/01/${String(currentYear - 1)}`,
        endDate: `12/31/${String(currentYear - 1)}`,
      };

    case DataRangeFilterEnum.OpenYear:
      return {
        startDate: `01/01/${String(currentYear)}`,
        endDate: formatUsLocaleDate(today),
      };

    case DataRangeFilterEnum.AllTime:
      return {
        startDate: ALL_TIME_START_DATE,
        endDate: formatUsLocaleDate(today),
      };

    case DataRangeFilterEnum.Custom:
      return {
        startDate: formatUsLocaleDate(today),
        endDate: formatUsLocaleDate(today),
      };

    default:
      throw new Error('Invalid date range filter');
  }
}
